import { Component, Inject, LOCALE_ID, OnDestroy } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppVersionService, IVersion } from "@services/app-version/app-version.service";
import { BrowserBackGuardService } from "@services/browser-back-guard/browser-back-guard.service";
import { ModalComponent } from "@shared/elements/modal/modal.component";
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { CURRENT_LANGUAGE_STORAGE_KEY } from '@services/language-options/language-options.service';
import { Subscription } from 'rxjs';
import { DeveloperConsoleComponent } from '@shared/elements/developer-console/developer-console.component';
import { developerConsole } from '@services/developer-console/developer-console.store';

type OfficeLike = {
    context?: {
        displayLanguage?: string;
        contentLanguage?: string;
    };
    onReady?: (callback: () => void) => void;
};

const SUPPORTED_LANGUAGES = ['en', 'es', 'pt', 'ru', 'uk'] as const;

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [
        ModalComponent,
        TranslateModule,
        CdkTrapFocus,
        RouterOutlet,
        DeveloperConsoleComponent,
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    providers: []
})
export class AppComponent implements OnDestroy {
    public readonly developerConsoleEnabled = developerConsole.enabled;
    public isShowVersionModal = false
    public appVersion: IVersion = {
        actual: '',
        new: ''
    }
    private readonly office = (globalThis as typeof globalThis & { Office?: OfficeLike }).Office;
    private versionSubscription?: Subscription;

    constructor(
        private translateService: TranslateService,
        private appVersionService: AppVersionService,
        private browserBackGuardService: BrowserBackGuardService,
        @Inject(LOCALE_ID) public locale: string | undefined
    ) {
        this.initAppVersion()
        this.initLanguage();
        this.browserBackGuardService.enable();
    }

    private initLanguage(): void {
        const storedLanguage = this.getSupportedLanguage(localStorage.getItem(CURRENT_LANGUAGE_STORAGE_KEY));
        const baseCandidates: Array<string | undefined> = [
            ...this.getOfficeLanguageCandidates(),
            ...(globalThis.navigator?.languages ?? []),
            this.locale,
            'en'
        ];

        if (storedLanguage) {
            this.applyLanguage(storedLanguage, baseCandidates);
            return;
        }

        const officeLanguage = this.getFirstSupportedLanguage(this.getOfficeLanguageCandidates());

        if (officeLanguage) {
            this.applyLanguage(officeLanguage, baseCandidates);
            return;
        }

        if (this.office?.onReady) {
            this.office.onReady(() => {
                this.applyLanguage(this.resolveLanguage([
                    ...this.getOfficeLanguageCandidates(),
                    ...(globalThis.navigator?.languages ?? []),
                    this.locale,
                    'en'
                ]), baseCandidates);
            });
            return;
        }

        const intlLocale = Intl.DateTimeFormat().resolvedOptions().locale;
        this.applyLanguage(this.resolveLanguage([
            ...(globalThis.navigator?.languages ?? []),
            this.getSupportedLanguage(intlLocale),
            this.locale,
            'en'
        ]), baseCandidates);
    }

    private applyLanguage(language?: string, candidates: Array<string | undefined> = []): void {
        const lang = this.resolveLanguage([language, 'en']);
        const supportedLanguages = this.getSupportedLanguages([lang, ...candidates, 'en']);

        this.translateService.addLangs(supportedLanguages);
        this.translateService.setDefaultLang('en');
        this.translateService.use(lang);
        localStorage.setItem(CURRENT_LANGUAGE_STORAGE_KEY, lang);
    }

    private getOfficeLanguageCandidates(): string[] {
        const officeLanguages = [
            this.office?.context?.displayLanguage,
            this.office?.context?.contentLanguage
        ];

        return officeLanguages.flatMap((language) =>
            language
                ?.split(/[,;]/)
                .map((item) => item.trim())
                .filter(Boolean) ?? []
        );
    }

    private resolveLanguage(languages: Array<string | undefined>): string {
        return this.getFirstSupportedLanguage(languages) ?? 'en';
    }

    private getSupportedLanguage(language?: string | null): string | undefined {
        const normalizedLanguage = language?.split('-')[0].toLowerCase().slice(0, 2);

        if (!normalizedLanguage) {
            return undefined;
        }

        return SUPPORTED_LANGUAGES.includes(normalizedLanguage as typeof SUPPORTED_LANGUAGES[number])
            ? normalizedLanguage
            : undefined;
    }

    private getSupportedLanguages(languages: Array<string | undefined>): string[] {
        const unique = new Set<string>();

        for (const language of languages) {
            const supportedLanguage = this.getSupportedLanguage(language);

            if (supportedLanguage) {
                unique.add(supportedLanguage);
            }
        }

        if (unique.size === 0) {
            unique.add('en');
        }

        return Array.from(unique);
    }

    private getFirstSupportedLanguage(languages: Array<string | undefined>): string | undefined {
        for (const language of languages) {
            const supportedLanguage = this.getSupportedLanguage(language);

            if (supportedLanguage) {
                return supportedLanguage;
            }
        }

        return undefined;
    }


    private subscribeOnUpdateVersion(isVersionOld: IVersion | null) {
        this.isShowVersionModal = !!isVersionOld
        if (isVersionOld) {
            this.appVersion = isVersionOld
        }
    }

    private initAppVersion(): void {
        this.versionSubscription = this.appVersionService.getIsVersionOld()
            .subscribe(this.subscribeOnUpdateVersion.bind(this));
        this.appVersionService.start();
    }

    public dismissUpdate(): void {
        this.appVersionService.dismiss();
    }

    public ngOnDestroy(): void {
        this.versionSubscription?.unsubscribe();
        this.appVersionService.stop();
    }

    protected readonly window = window;

    public updateApp(): void {
        const url = new URL(window.location.href);
        url.searchParams.set('time', Date.now().toString());
        window.location.href = url.toString();
    }
}
